import React, { useEffect, useState, useCallback } from 'react';
import { getDashboard, getSettings } from '../api';
import { formatDuration, getInitials, scoreColor, weekRange } from '../utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../AuthContext';

// Calculate remaining hours for week and month for a single employee row
function calcPersonalStats(empRow, expectedWorkHours) {
  if (!empRow) return null;

  const now = new Date();

  // ── Week remaining ──────────────────────────────────────────────────────
  // Days left in the ISO work week (Mon=1 … Fri=5), capped at weekdays only
  const dayOfWeek = now.getDay(); // 0=Sun,1=Mon…6=Sat
  const daysLeftInWeek = Math.max(0, 5 - (dayOfWeek === 0 ? 5 : dayOfWeek - 1));
  const totalWeekSeconds = expectedWorkHours * 5 * 3600;
  const weekDoneSeconds = empRow.total_seconds || 0;
  const weekRemainingSecs = Math.max(0, totalWeekSeconds - weekDoneSeconds);

  // ── Month remaining ─────────────────────────────────────────────────────
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  // Count remaining weekdays in month (including today if before end of day)
  let remainingWeekdays = 0;
  for (let d = now.getDate(); d <= daysInMonth; d++) {
    const wd = new Date(now.getFullYear(), now.getMonth(), d).getDay();
    if (wd !== 0 && wd !== 6) remainingWeekdays++;
  }
  // Hours logged this month (use week data as proxy for now — dashboard only gives week)
  // We show expected remaining = remainingWeekdays * expectedWorkHours
  const monthRemainingHours = remainingWeekdays * expectedWorkHours;

  return {
    weekDoneHours: (weekDoneSeconds / 3600).toFixed(1),
    weekTotalHours: (totalWeekSeconds / 3600).toFixed(0),
    weekRemainingHours: (weekRemainingSecs / 3600).toFixed(1),
    weekProgress: Math.min(100, Math.round((weekDoneSeconds / totalWeekSeconds) * 100)),
    monthRemainingHours: monthRemainingHours.toFixed(0),
    remainingWeekdays,
  };
}

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expectedWorkHours, setExpectedWorkHours] = useState(8);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = weekRange();
      const [res, settings] = await Promise.all([
        getDashboard({ from, to }),
        getSettings().catch(() => ({})),
      ]);
      if (settings.expected_work_hours) setExpectedWorkHours(parseInt(settings.expected_work_hours) || 8);
      // Employees only see their own data
      if (!isAdmin && user?.employee_id) {
        res.employees = res.employees.filter(e => e.employee.id === user.employee_id);
      }
      setData(res);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [isAdmin, user?.employee_id]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (loading && !data) return <div className="empty">Loading dashboard...</div>;
  if (!data) return <div className="empty">No data</div>;

  const totalActive = data.employees.filter(e => e.is_active).length;
  const totalHours = data.employees.reduce((s, e) => s + e.total_seconds, 0) / 3600;
  const avgProductivity = data.employees.length
    ? Math.round(data.employees.reduce((s, e) => s + e.productivity_score, 0) / data.employees.length)
    : 0;

  // Personal stats for employee/manager view
  const myRow = !isAdmin ? data.employees.find(e => e.employee.id === user?.employee_id) || data.employees[0] : null;
  const personal = calcPersonalStats(myRow, expectedWorkHours);

  const chartData = data.employees.map(e => ({
    name: e.employee.name.split(' ')[0],
    hours: +(e.total_seconds / 3600).toFixed(1),
    score: e.productivity_score,
  }));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <span className="text-muted" style={{ fontSize: '0.875rem' }}>This week</span>
      </div>

      {/* Personal hours cards — employee & manager only */}
      {!isAdmin && personal && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          {/* Card 1: Week progress */}
          <div className="card" style={{ borderLeft: '4px solid var(--primary)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>This Week</div>
              <span style={{ fontSize: '0.75rem', color: 'var(--muted)', background: 'var(--bg)', padding: '2px 8px', borderRadius: 99 }}>
                {personal.weekProgress}% done
              </span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--primary)', lineHeight: 1 }}>
              {personal.weekRemainingHours}<span style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--muted)', marginLeft: 4 }}>hrs left</span>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '6px 0 10px' }}>
              {personal.weekDoneHours}h done of {personal.weekTotalHours}h target
            </div>
            {/* Progress bar */}
            <div style={{ height: 6, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 99,
                width: `${personal.weekProgress}%`,
                background: personal.weekProgress >= 100 ? 'var(--success)' : personal.weekProgress >= 60 ? 'var(--primary)' : 'var(--warning)',
                transition: 'width .5s',
              }} />
            </div>
          </div>

          {/* Card 2: Month remaining */}
          <div className="card" style={{ borderLeft: '4px solid var(--accent)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>This Month</div>
              <span style={{ fontSize: '0.75rem', color: 'var(--muted)', background: 'var(--bg)', padding: '2px 8px', borderRadius: 99 }}>
                {personal.remainingWeekdays} days left
              </span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--accent)', lineHeight: 1 }}>
              {personal.monthRemainingHours}<span style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--muted)', marginLeft: 4 }}>hrs remaining</span>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 6 }}>
              Based on {expectedWorkHours}h/day × {personal.remainingWeekdays} remaining workdays
            </div>
          </div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Active Now</div>
          <div className="stat-value" style={{ color: totalActive > 0 ? 'var(--success)' : 'var(--muted)' }}>{totalActive}</div>
          <div className="stat-sub">of {data.employees.length} employees</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Hours</div>
          <div className="stat-value">{totalHours.toFixed(1)}</div>
          <div className="stat-sub">this week</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Productivity</div>
          <div className="stat-value" style={{ color: scoreColor(avgProductivity) }}>{avgProductivity}%</div>
          <div className="stat-sub">team average</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Team Size</div>
          <div className="stat-value">{data.employees.length}</div>
          <div className="stat-sub">active employees</div>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 20 }}>
        <div className="card">
          <div className="card-title">Hours This Week</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => [`${v}h`, 'Hours']} />
              <Bar dataKey="hours" fill="#4f46e5" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-title">Productivity Scores</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => [`${v}%`, 'Score']} />
              <Bar dataKey="score" fill="#10b981" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card mt-16">
        <div className="card-title">Team Status</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Status</th>
                <th>Hours This Week</th>
                <th>Sessions</th>
                <th>Productivity</th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map(emp => (
                <tr key={emp.employee.id}>
                  <td>
                    <div className="flex gap-8" style={{ alignItems: 'center' }}>
                      <div className="employee-avatar">{getInitials(emp.employee.name)}</div>
                      <div>
                        <div style={{ fontWeight: 500 }}>{emp.employee.name}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{emp.employee.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{emp.employee.department || '—'}</td>
                  <td>
                    <span className={`badge ${emp.is_active ? 'badge-green' : 'badge-gray'}`}>
                      {emp.is_active ? '● Online' : '○ Offline'}
                    </span>
                  </td>
                  <td>{(emp.total_seconds / 3600).toFixed(1)}h</td>
                  <td>{emp.sessions}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="score-bar" style={{ width: 80 }}>
                        <div className="score-fill" style={{ width: `${emp.productivity_score}%`, background: scoreColor(emp.productivity_score) }} />
                      </div>
                      <span style={{ fontWeight: 600, color: scoreColor(emp.productivity_score) }}>{emp.productivity_score}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
